import { supabase } from '@/lib/supabase';
import type { Trip, TripFilters } from '../types';

interface TripRow {
  id: string;
  business_id: string;
  project_id: string | null;
  driver_id: string | null;
  start_time: string;
  end_time: string | null;
  start_address_text: string | null;
  end_address_text: string | null;
  distance_km: number;
  distance_source: string;
  rate_per_km: number;
  calculated_cost: number;
  billable: boolean;
  billing_status: string;
  assignment_status: string;
  suggested_project_id: string | null;
  match_location: string | null;
  match_confidence: string | null;
  match_distance_m: number | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  driver_name: string | null;
  project_name: string | null;
  customer_name: string | null;
  suggested_project_name: string | null;
  suggested_customer_name: string | null;
}

function mapRowToTrip(row: TripRow): Trip {
  return {
    id: row.id,
    businessId: row.business_id,
    projectId: row.project_id,
    driverId: row.driver_id,
    startTime: row.start_time,
    endTime: row.end_time,
    startAddressText: row.start_address_text,
    endAddressText: row.end_address_text,
    distanceKm: row.distance_km,
    distanceSource: row.distance_source,
    ratePerKm: row.rate_per_km,
    calculatedCost: row.calculated_cost,
    billable: row.billable,
    billingStatus: row.billing_status as Trip['billingStatus'],
    assignmentStatus: row.assignment_status as Trip['assignmentStatus'],
    suggestedProjectId: row.suggested_project_id,
    matchLocation: row.match_location as Trip['matchLocation'],
    matchConfidence: row.match_confidence as Trip['matchConfidence'],
    matchDistanceM: row.match_distance_m,
    processedAt: row.processed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    driverName: row.driver_name,
    projectName: row.project_name,
    customerName: row.customer_name,
    suggestedProjectName: row.suggested_project_name,
    suggestedCustomerName: row.suggested_customer_name,
  };
}

export async function fetchTrips(
  businessId: string,
  filters?: TripFilters
): Promise<Trip[]> {
  let query = supabase
    .from('v_trips_with_assignment')
    .select('*')
    .eq('business_id', businessId)
    .order('start_time', { ascending: false });

  if (filters?.assignmentStatus?.length) {
    query = query.in('assignment_status', filters.assignmentStatus);
  }

  if (filters?.billingStatus?.length) {
    query = query.in('billing_status', filters.billingStatus);
  }

  if (filters?.projectId) {
    query = query.eq('project_id', filters.projectId);
  }

  if (filters?.startDate) {
    query = query.gte('start_time', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('start_time', filters.endDate);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching trips:', error);
    throw new Error(error.message);
  }

  return (data || []).map(mapRowToTrip);
}

export async function fetchPendingTrips(businessId: string): Promise<Trip[]> {
  const { data, error } = await supabase
    .from('v_trips_with_assignment')
    .select('*')
    .eq('business_id', businessId)
    .eq('assignment_status', 'pending_review')
    .is('project_id', null)
    .order('start_time', { ascending: false });

  if (error) {
    console.error('Error fetching pending trips:', error);
    throw new Error(error.message);
  }

  return (data || []).map(mapRowToTrip);
}

export async function assignTripToProject(
  tripId: string,
  projectId: string
): Promise<void> {
  const { error } = await supabase
    .from('mileage_trips')
    .update({
      project_id: projectId,
      assignment_status: 'manually_assigned',
    })
    .eq('id', tripId);

  if (error) {
    console.error('Error assigning trip:', error);
    throw new Error(error.message);
  }
}

export async function dismissTrip(tripId: string): Promise<void> {
  const { error } = await supabase
    .from('mileage_trips')
    .update({
      assignment_status: 'unassigned',
    })
    .eq('id', tripId);

  if (error) {
    console.error('Error dismissing trip:', error);
    throw new Error(error.message);
  }
}

export async function unassignTrip(tripId: string): Promise<void> {
  const { error } = await supabase
    .from('mileage_trips')
    .update({
      project_id: null,
      assignment_status: 'pending_review',
    })
    .eq('id', tripId);

  if (error) {
    console.error('Error unassigning trip:', error);
    throw new Error(error.message);
  }
}